import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { areEqual } from 'Utils/equalityChecks';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { setToaster } from 'Containers/Core/actions';
import { SpinnerBlock } from 'Components/SpinnerBlock';
import { PurpleButton } from 'Components/Button';
import { RocketDryRoomHeader } from 'Components/RocketDry';
import { SerializedEquipmentPool, CheckOutModal } from 'Components/SerializedEquipment';

import { AssetHistoryModal } from '../AssetHistoryModal/AssetHistoryModal';
import { DeployAssetModal } from '../DeployAssetModal/DeployAssetModal';
import { AddUnitToRoomModal } from '../AddUnitToRoomModal/AddUnitToRoomModal';
import { listRoomEquipmentAssets, checkOutAsset, getEquipmentAsset, updatePlacement } from '../actions';
import { EquipmentAsset } from '../types';

import classes from './serializedRoomEquipment.module.css';

interface Props {
  room: any;
}

// Per-room serialized equipment inside the project RocketDry Equipment Log (shown when
// `serializedEquipment` is ON). Lists the units currently deployed in this room and supports adding
// an available unit, moving, checking out, and viewing history — the per-project counterpart of the
// company-wide inventory workspace.
function SerializedRoomEquipmentContainer({ room }: Props) {
  const dispatch = useDispatch();
  const companyId = useSelector(firstCompanyIdSelector, areEqual);

  const roomId: number = room?.id;
  const roomType: string = room?.room_type?.name || 'Room';

  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [historyAsset, setHistoryAsset] = useState<EquipmentAsset | null>(null);
  const [moveTarget, setMoveTarget] = useState<EquipmentAsset | null>(null);
  const [checkoutTarget, setCheckoutTarget] = useState<EquipmentAsset | null>(null);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [editBusyId, setEditBusyId] = useState<number | null>(null); // placement date-edit in flight

  // One checkout key per unit, reused across retries until it succeeds (backend dedupes replays).
  const checkoutKeys = useRef<Record<number, string>>({});

  const load = useCallback(async () => {
    if (!roomId) {
      setFetching(false);
      return;
    }
    setFetching(true);
    setFailed(false);
    const response: any = await dispatch(listRoomEquipmentAssets(roomId));
    if (!response) {
      setFailed(true);
      setFetching(false);
      return;
    }
    setAssets(response?.data ?? []);
    setFetching(false);
  }, [roomId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const onHistory = useCallback((asset: EquipmentAsset) => setHistoryAsset(asset), []);
  const onCloseHistory = useCallback(() => setHistoryAsset(null), []);

  const onMove = useCallback((asset: EquipmentAsset) => setMoveTarget(asset), []);
  const onCloseMove = useCallback(() => setMoveTarget(null), []);
  const onMoveSuccess = useCallback(() => {
    setMoveTarget(null);
    load(); // the unit left this room
  }, [load]);
  // Move hit a 409 (stale updated_at): refresh just that unit so the retry carries the current token.
  const onMoveConflict = useCallback(async () => {
    if (!moveTarget) return;
    const fresh: any = await dispatch(getEquipmentAsset(moveTarget.id));
    if (fresh?.data) {
      setAssets((prev) => prev.map((item) => (item.id === fresh.data.id ? fresh.data : item)));
      setMoveTarget(fresh.data);
    }
  }, [moveTarget, dispatch]);

  const onCheckOut = useCallback((asset: EquipmentAsset) => {
    setCheckoutError(null);
    setCheckoutTarget(asset);
  }, []);
  const onCloseCheckout = useCallback(() => setCheckoutTarget(null), []);
  const onConfirmCheckout = useCallback(
    async (dateOut?: string) => {
      const asset = checkoutTarget;
      if (!asset) return;
      const idempotencyKey = checkoutKeys.current[asset.id] ?? uuidv4();
      checkoutKeys.current[asset.id] = idempotencyKey;
      setCheckoutSubmitting(true);
      setCheckoutError(null);
      const response: any = await dispatch(
        checkOutAsset(asset.id, { updated_at: asset.updated_at, date_out: dateOut, idempotency_key: idempotencyKey })
      );
      setCheckoutSubmitting(false);
      if (response?.data) {
        delete checkoutKeys.current[asset.id];
        setCheckoutTarget(null);
        load(); // the unit left this room
        return;
      }
      // On a 409 the updated_at is stale — refresh that unit and re-point the modal so the retry
      // carries the current token.
      if (response?.error?.status === 409) {
        const fresh: any = await dispatch(getEquipmentAsset(asset.id));
        if (fresh?.data) {
          setAssets((prev) => prev.map((item) => (item.id === asset.id ? fresh.data : item)));
          setCheckoutTarget(fresh.data);
        }
      }
      setCheckoutError(response?.error?.message || 'Couldn’t check out this unit. Please try again.');
    },
    [checkoutTarget, dispatch, load]
  );

  const onAddClick = useCallback(() => setAddOpen(true), []);
  const onCloseAdd = useCallback(() => setAddOpen(false), []);

  // Edit the open placement's deploy date inline. Optimistic-locked on the placement's own
  // updated_at; on any failure we refresh the row and surface the backend 409/422 message.
  const onEditDate = useCallback(
    async (asset: EquipmentAsset, dateIn: string) => {
      const placement = asset.current_placement;
      if (!placement || !dateIn) return;
      setEditBusyId(asset.id);
      const response: any = await dispatch(
        updatePlacement(placement.id, { date_in: dateIn, updated_at: placement.updated_at })
      );
      setEditBusyId(null);
      if (response?.data) {
        load();
        return;
      }
      if (response?.error?.status === 409) load(); // stale — refresh so a retry carries the new token
      dispatch(setToaster(response?.error?.message || 'Couldn’t update the deploy date. Please try again.', false));
    },
    [dispatch, load]
  );

  const busyId = editBusyId ?? (checkoutSubmitting ? checkoutTarget?.id ?? null : null);

  return (
    <div data-testid="serialized-room-equipment">
      <RocketDryRoomHeader icon={roomType} title={roomType} />

      <div className={classes.actions}>
        {/* Don't allow deploying into a room whose current contents we can't see (loading/failed). */}
        <PurpleButton type="button" className={classes.addButton} disabled={fetching || failed} onClick={onAddClick}>
          Add equipment +
        </PurpleButton>
      </div>

      {failed && (
        <div className="d-flex flex-column align-items-center p-3" role="alert" data-testid="room-equipment-error">
          <p className="text-muted mb-2">Couldn’t load this room’s equipment.</p>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={load}>
            Retry
          </button>
        </div>
      )}
      {!failed && fetching && <SpinnerBlock fetching />}
      {!failed && !fetching && (
        <SerializedEquipmentPool
          assets={assets}
          busyAssetId={busyId}
          onEditDate={onEditDate}
          onHistory={onHistory}
          onMove={onMove}
          onCheckOut={onCheckOut}
        />
      )}

      <AssetHistoryModal isOpen={!!historyAsset} asset={historyAsset} onClose={onCloseHistory} />

      <CheckOutModal
        isOpen={!!checkoutTarget}
        asset={checkoutTarget}
        submitting={checkoutSubmitting}
        submitError={checkoutError}
        onConfirm={onConfirmCheckout}
        onClose={onCloseCheckout}
      />

      <DeployAssetModal
        isOpen={!!moveTarget}
        asset={moveTarget}
        mode="move"
        onClose={onCloseMove}
        onSuccess={onMoveSuccess}
        onConflict={onMoveConflict}
      />

      <AddUnitToRoomModal
        isOpen={addOpen}
        companyId={companyId}
        roomId={roomId}
        onClose={onCloseAdd}
        onSuccess={load}
      />
    </div>
  );
}

const SerializedRoomEquipmentMemo = memo(SerializedRoomEquipmentContainer, areEqual);

export { SerializedRoomEquipmentMemo as SerializedRoomEquipment };
